import type { ComponentType } from 'react';

export type RouteLoader = () => Promise<{ default: ComponentType }>;

/* ── Route loaders: single source of truth for route-chunk imports ────
   App.tsx wraps these in React.lazy for rendering; warmRoute() invokes
   them directly to start fetching a chunk before React asks for it
   (on first load for the landing route, and on hover/focus from Home).
   ──────────────────────────────────────────────────────────────────── */

export const homeLoader: RouteLoader = () => import('./tools/home/Home');
export const imageShareLoader: RouteLoader = () => import('./tools/imageshare/ImageShare');
export const fileShareLoader: RouteLoader = () => import('./tools/fileshare/FileShare');
export const formsLoader: RouteLoader = () => import('./tools/forms/Forms');
export const schedulerLoader: RouteLoader = () => import('./tools/scheduler/Scheduler');
export const photographyLoader: RouteLoader = () => import('./tools/photography/Photography');
export const blogPostLoader: RouteLoader = () => import('./tools/blog/BlogPostContent');
export const linkTreeLoader: RouteLoader = () => import('./tools/linktree/LinkTree');
export const redirectLoader: RouteLoader = () => import('./tools/redirect/Redirect');

export const toolLoaders: [string, RouteLoader][] = [
  ['/speedreader',              () => import('./tools/speedreader/SpeedReader')],
  ['/joluno/links',             () => import('./tools/joluno/JoLuno')],
  ['/joluno',                   () => import('./tools/joluno-landing/JoLunoLanding')],
  ['/vote',                     () => import('./tools/vote/Vote')],
  ['/scheduler',                schedulerLoader],
  ['/forms',                    formsLoader],
  ['/pomodoro',                 () => import('./tools/pomodoro/Pomodoro')],
  ['/markdown',                 () => import('./tools/markdown/Markdown')],
  ['/json',                     () => import('./tools/json/JsonFormatter')],
  ['/colors',                   () => import('./tools/colors/Colors')],
  ['/qr',                       () => import('./tools/qr/QrCode')],
  ['/base64',                   () => import('./tools/base64/Base64')],
  ['/units',                    () => import('./tools/units/Units')],
  ['/timezone',                 () => import('./tools/timezone/Timezone')],
  ['/epoch',                    () => import('./tools/epoch/Epoch')],
  ['/typing',                   () => import('./tools/typing/Typing')],
  ['/decide',                   () => import('./tools/decide/Decide')],
  ['/beatviz',                  () => import('./tools/beatviz/BeatViz')],
  ['/lookup',                   () => import('./tools/lookup/Lookup')],
  ['/diff',                     () => import('./tools/diff/Diff')],
  ['/regex',                    () => import('./tools/regex/RegexTester')],
  ['/gradient',                 () => import('./tools/gradient/Gradient')],
  ['/tessellate',               () => import('./tools/tessellate/Tessellate')],
  ['/hash',                     () => import('./tools/hash/Hash')],
  ['/password',                 () => import('./tools/password/Password')],
  ['/photo',                    () => import('./tools/photo/Photo')],
  ['/colorpick',                () => import('./tools/colorpick/ColorPick')],
  ['/moodboard',                () => import('./tools/moodboard/Moodboard')],
  ['/tone',                     () => import('./tools/tone/Tone')],
  ['/exif',                     () => import('./tools/exif/ExifViewer')],
  ['/suggest',                  () => import('./tools/suggest/Suggest')],
  ['/selfielight',              () => import('./tools/selfielight/SelfieLight')],
  ['/multicamera',              () => import('./tools/multicamera/MultiCamera')],
  ['/bugreport',                () => import('./tools/bugreport/BugReport')],
  ['/boosted-triggered-agents', () => import('./tools/boosted/BoostedTriggeredAgents')],
  ['/excerpt',                  () => import('./tools/excerpt/Excerpt')],
  ['/noise',                    () => import('./tools/noise/Noise')],
  ['/metronome',                () => import('./tools/metronome/Metronome')],
  ['/split',                    () => import('./tools/split/Split')],
  ['/flashcards',               () => import('./tools/flashcards/Flashcards')],
  ['/textstats',                () => import('./tools/textstats/TextStats')],
  ['/redirect',                 redirectLoader],
  ['/fishbowl',                 () => import('./tools/fishbowl/Fishbowl')],
  ['/imposter',                 () => import('./tools/imposter/Imposter')],
  ['/teams',                    () => import('./tools/teams/TeamPicker')],
  ['/bracket',                  () => import('./tools/bracket/Bracket')],
  ['/secretsanta',              () => import('./tools/secretsanta/SecretSanta')],
  ['/fonts',                    () => import('./tools/fonts/FontPreview')],
  ['/sleep',                    () => import('./tools/sleep/SleepCalc')],
  ['/mortgage',                 () => import('./tools/mortgage/Mortgage')],
  ['/stopwatch',                () => import('./tools/stopwatch/Stopwatch')],
  ['/aspect',                   () => import('./tools/aspect/AspectRatio')],
  ['/voice',                    () => import('./tools/voice/VoiceChanger')],
  ['/tts',                      () => import('./tools/tts/TextToSpeech')],
  ['/timer',                    () => import('./tools/timer/Timer')],
  ['/fancytext',                () => import('./tools/fancytext/FancyText')],
  ['/grades',                   () => import('./tools/grades/Grades')],
  ['/about',                    () => import('./tools/about/About')],
  ['/blog',                     () => import('./tools/blog/Blog')],
  ['/status',                   () => import('./tools/status/Status')],
  ['/status/red',               () => import('./tools/status/StatusChaos')],
  ['/imageshare',               imageShareLoader],
  ['/fileshare',                fileShareLoader],
  ['/ambient',                  () => import('./tools/ambient/Ambient')],
  ['/doopey3d',                 () => import('./tools/doopey3d/Doopey3DLab')],
  ['/aliencat',                 () => import('./tools/aliencat/AlienCat')],
  ['/urlencode',                () => import('./tools/urlencode/UrlEncode')],
  ['/cron',                     () => import('./tools/cron/CronHelper')],
  ['/lorem',                    () => import('./tools/lorem/Lorem')],
  ['/fontren',                  () => import('./tools/fontren/Fontren')],
  ['/links/:slug',              linkTreeLoader],
];

/* Param routes can't be matched by exact path — warm by prefix. */
const prefixLoaders: [string, RouteLoader][] = [
  ['/blog/',        blogPostLoader],
  ['/photography',  photographyLoader],
  ['/i/',           imageShareLoader],
  ['/f/',           fileShareLoader],
  ['/t/',           formsLoader],
  ['/scheduler/',   schedulerLoader],
  ['/links/',       linkTreeLoader],
  ['/redirect/',    redirectLoader],
];

/**
 * Start fetching the chunk for a route without waiting for React to render
 * its <Route>. Failures are swallowed — React.lazy re-imports on render and
 * surfaces real errors through the chunk error boundary.
 */
export function warmRoute(pathname: string): void {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  let load: RouteLoader | undefined;
  if (path === '/') load = homeLoader;
  else load = toolLoaders.find(([p]) => p === path)?.[1]
    ?? prefixLoaders.find(([prefix]) => path.startsWith(prefix))?.[1];
  if (load) void load().catch(() => {});
}
